<template>
  <base-view title="Criar promoção digital" class="mb-5">
    <b-row class="mb-5">
      <b-col cols="6" lg="4">
        <label for="">Cor de fundo</label>
        <chrome-picker v-model="customization.backgroundColor" />
      </b-col>
      <b-col cols="6" lg="4">
        <label for="">Cor do cabeçalho</label>
        <chrome-picker v-model="customization.headerColor" />
      </b-col>
      <b-col cols="6" lg="4">
        <label for="">Cor da fonte</label>
        <chrome-picker v-model="customization.fontColor" />
      </b-col>
      <b-col cols="12" class="mt-4">
        <b-row>
          <b-col cols="12" class="mb-2">
            <label for="">Rede social</label>
            <select-items
              label="name"
              v-model="fields.socialMedia"
              :items="templates"
              :reduce-function="(item) => item.value"
              @update:value="handleSocialMediaSelection"
            />
          </b-col>
          <b-col cols="12" lg="6" class="mb-3">
            <label for="">Largura (px)</label>
            <b-input
              no-wheel
              type="number"
              placeholder="Largura (px)"
              v-model="fields.width"
              :disabled="!!fields.socialMedia"
            ></b-input>
          </b-col>
          <b-col cols="12" lg="6" class="mb-3">
            <label for="">Altura (px)</label>
            <b-input
              no-wheel
              type="number"
              placeholder="Altura (px)"
              v-model="fields.height"
              :disabled="!!fields.socialMedia"
            ></b-input>
          </b-col>
          <b-col cols="12" class="mb-3">
            <label for="">Valido até</label>
            <b-form-datepicker
              v-model="fields.untilDate"
              reset-button
              close-button
              today-button
              :date-format-options="{
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
              }"
              placeholder="Valido até"
              label-close-button="Fechar"
              label-today-button="Hoje"
              label-reset-button="Limpar"
              label-help=""
              locale="pt-BR"
            />
          </b-col>
          <b-col cols="12">
            <label for="">Nome do arquivo</label>
            <b-input
              placeholder="Nome do arquivo"
              v-model="fields.fileName"
            ></b-input>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
    <b-row>
      <b-col cols="12">
        <b-row align-content="center" align-v="center">
          <b-col cols="12" class="mb-4">
            <b-input
              placeholder="Pesquise pelo nome dos produtos"
              debounce="1000"
              v-model="fields.productName"
              @keydown.enter="getProducts"
            >
            </b-input>
          </b-col>
          <b-col
            cols="12"
            v-if="!loadingProducts && (!products || !products.length)"
          >
            <b-card class="mx-auto" style="max-width: 220px">
              <b-card-text
                class="d-flex flex-column align-items-center text-center"
              >
                <i class="fas fa-info-circle fs-20 text-info mb-2"></i>
                Sem produtos encontrados
              </b-card-text>
            </b-card>
          </b-col>
          <b-col
            v-if="loadingProducts"
            cols="12"
            class="d-flex flex-column align-items-center mb-4"
          >
            <b-spinner label="Spinning" class="mb-2"> </b-spinner>
            Carregando produtos...
          </b-col>
          <b-col
            v-else
            class="d-flex align-items-center flex-wrap"
            style="gap: 10px"
          >
            <div
              class="d-flex"
              style="flex: 1 0 170px"
              v-for="product in products"
              :key="product.id"
            >
              <ProductCard
                style="flex: 0 0 100%"
                :product="product"
                :selectedProducts="selectedProducts"
                @select="handleProductSelection(product)"
                @updatePrice="$set(product, 'priceSalesFormat1', $event)"
              />
            </div>
          </b-col>
          <b-col cols="12">
            <b-pagination
              v-if="
                pagination.total > 1 &&
                !loadingProducts &&
                pagination.total > pagination.itemsPerPage
              "
              v-model="pagination.currentPage"
              :total-rows="pagination.total"
              :per-page="pagination.itemsPerPage"
            ></b-pagination>
          </b-col>
        </b-row>
      </b-col>
      <b-col cols="8">
        <ImagePreview
          :id="idKey"
          :companyInfo="companyInfo"
          :productsToRender="productsToRender"
          :productsPhotos="productPhotos"
          :width="fields.width"
          :height="fields.height"
          :backgroundColor="customization.backgroundColor"
          :headerColor="customization.headerColor"
          :fontColor="customization.fontColor"
          :untilDate="fields.untilDate"
          :socialMedias="socialMedias"
          :generatingImage="generatingImage"
          :limitOfProducts="limitOfProducts"
          @downloadImage="downloadImage"
          @excludeEdit="excludeEdit"
        />
      </b-col>
    </b-row>
  </base-view>
</template>

<script>
import * as htmlToImage from 'html-to-image';
import { Chrome } from 'vue-color';
import { mapActions, mapGetters, mapState } from 'vuex';
import api from '@/api';
import { downloadFileFromBase64, formatNumber } from '@/utils';
import { toDDMMYYYY } from '@/utils/date';
import { getImageLinkByOrigin } from '@/utils/photos';
import UserTypes from '@/enums/UserTypes';
import ImagePreview from './ImagePreview.vue';
import ProductCard from './ProductCard.vue';

export default {
  components: {
    'chrome-picker': Chrome,
    ImagePreview,
    ProductCard,
  },
  data() {
    return {
      idKey: 'digitalPromotionImageElementId',
      productPhotos: {},
      companyInfo: {},
      selectedProducts: {},
      pagination: {
        currentPage: 1,
        itemsPerPage: 1,
        total: 1,
        lastPage: 1,
      },
      fields: {
        fileName: '',
        productName: '',
        width: 800,
        height: 800,
        untilDate: '',
        socialMedia: '',
      },
      customization: {
        backgroundColor: {
          hex: '#F2F2F2',
        },
        headerColor: {
          hex: '#F8E66D',
        },
        fontColor: {
          hex: '#393939',
        },
      },
      socialMedias: [],
      products: [],
      productsToRender: [],
      templates: [
        {
          name: 'Instagram Post',
          value: '1080x1080',
          productsLimit: 16,
        },
        {
          name: 'Instagram Story',
          value: '1080x1920',
          productsLimit: 24,
        },
        {
          name: 'Facebook Post',
          value: '940x788',
          productsLimit: 8,
        },
      ],
      loadingProducts: false,
      generatingImage: false,
    };
  },
  computed: {
    ...mapState(['dadosUsuario']),
    ...mapGetters('general', ['getImagesAddressApp']),
    limitOfProducts() {
      const limitOfProducts = this.templates.find(
        (x) => x.value === this.fields.socialMedia
      );
      const defaultLimit = 6;

      return limitOfProducts ? limitOfProducts.productsLimit : defaultLimit;
    },
  },
  watch: {
    'fields.productName': function () {
      this.getProducts();
    },
    'pagination.currentPage': function () {
      this.getProducts();
    },
  },
  mounted() {
    const name = this.$route.meta.userType == UserTypes.BALCONISTA ? 
    this.dadosUsuario.lojista.nome_fantasia : 
    this.dadosUsuario.informacoes.razao_social;
    
    this.fields.fileName = name;
    this.getProducts();
    this.getMedias();
    this.getCompanyInfo();
  },
  methods: {
    formatNumber,
    toDDMMYYYY,
    getImageLinkByOrigin,
    ...mapActions('shopkeeper', [
      'getSelectedProducts',
      'getSocialMedias',
      'getAboutCompany',
    ]),
    downloadImage() {
      this.generatingImage = true;

      htmlToImage
        .toJpeg(document.getElementById(this.idKey), {
          skipFonts: true,
          quality: 1,
          style: {
            width: this.fields.width,
            height: this.fields.height,
          },
        })
        .then((dataUrl) => {
          downloadFileFromBase64({
            base64: dataUrl,
            fileName: `${
              this.fields.fileName || this.dadosUsuario.informacoes.razao_social
            }.jpeg`,
          });
        })
        .finally(() => {
          this.generatingImage = false;
        });
    },
    async handleProductSelection(product) {
      this.$set(
        this.selectedProducts,
        product.id,
        !this.selectedProducts[product.id]
      );

      if (!this.selectedProducts[product.id]) {
        delete this.productPhotos[product.id];
        this.productsToRender = this.productsToRender.filter(
          (p) => p.id !== product.id
        );
        return;
      }

      this.productsToRender.push(product);

      /**
       * Precisamos do base64 pois a lib html-to-image não consegue renderizar
       * imagens que estão em um domínio diferente do que está sendo executado
       */
      const base64 = await this.getPhotoBase64(
        this.getImageLinkByOrigin(product.origem) + product.photo
      );

      this.$set(
        this.productPhotos,
        product.id,
        `data:image/jpeg;base64,${base64}`
      );
    },
    async getPhotoBase64(url) {
      const { data } = await api.get(`/get-file-base64?url=${url}`);

      return data.base64;
    },
    getProducts() {
      if (this.loadingProducts) return;

      this.loadingProducts = true;

      this.getSelectedProducts({
        userId: this.userTypeId(),
        productName: this.fields.productName,
        currentPage: this.pagination.currentPage,
        status: 0,
      })
        .then((response) => {
          const { data, perPage, total, lastPage } = response.data.data || {};
          this.pagination.itemsPerPage = perPage || 15;
          this.pagination.total = total;
          this.pagination.lastPage = lastPage;
          this.products = data || [];
        })
        .finally(() => {
          this.loadingProducts = false;
        });
    },
    async getMedias() {
      const { data } = await this.getSocialMedias(this.userTypeId());

      this.socialMedias = data.data || [];
    },
    async getCompanyInfo() {
      const { data } = await this.getAboutCompany(this.userTypeId());

      this.companyInfo = data.data || {};

      if (!this.companyInfo.logo) return;

      /**
       * Precisamos do base64 pois a lib html-to-image não consegue renderizar
       * imagens que estão em um domínio diferente do que está sendo executado
       */
      const base64 = await this.getPhotoBase64(
        this.getImagesAddressApp + this.companyInfo.logo
      );

      this.companyInfo.logoBase64 = `data:image/png;base64,${base64}`;
    },
    handleSocialMediaSelection() {
      if (!this.fields.socialMedia) return;

      const [width, height] = this.fields.socialMedia.split('x');

      this.fields.width = width;
      this.fields.height = height;
    },
    userTypeId(){
      return this.$route.meta.userType == UserTypes.BALCONISTA ? this.dadosUsuario.construp_user_id : this.dadosUsuario.id;
    },
    excludeEdit() {
      this.productsToRender = [];
      this.fields.fileName = '';
      this.fields = {};
      this.fields.width = 800;
      this.fields.height = 800;
      this.customization.backgroundColor = {
        hex: '#F2F2F2',
      };
      this.customization.headerColor = {
        hex: '#F8E66D',
      };
      this.customization.fontColor = {
        hex: '#393939',
      };
      this.selectedProducts = {};

      this.products.forEach((product) => {
        this.$set(this.selectedProducts, product.id, false);
      });
    },
  },
};
</script>